import * as React from 'react';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlug, faFileArrowUp } from '@awesome.me/kit-8a0d2fd588/icons/modules/classic/light';

import { Tag } from '../design-system/components';

import { ConnectorType } from '../../Store/ImportFinancials';

import { AccountingPackage, PortalTextImportModals } from '../../Models/Api/strongbox.financialportal';
import { ExcelUploadAccountingPackageNameId, UniversalCollectorAccountingPackageNameId } from '../../Models/Api/AccountingPackage';

type Props = {
    connectorType: ConnectorType;
    onChangeConnectorType: (type: ConnectorType) => void;
    importModalsText?: PortalTextImportModals;
    activeAccountingPackages: AccountingPackage[];
    fileConnectorType: ConnectorType;   // Will be either universal collector or excel.  Can't be both.
    // When true (V2 data-collection refactor enabled), show a small indicator below the cards.
};

// The supported file types shown under the upload card. These are format names, not localizable copy.
const SUPPORTED_FILE_TYPES = ['CSV', 'Excel'];

export const ChooseConnectorTypeModal: React.FC<Props> = (props): React.ReactElement => {
    const {
        connectorType,
        onChangeConnectorType,
        importModalsText,
        activeAccountingPackages,
        fileConnectorType,
    } = props;

    // The "supported accounting systems" list comes from the real packages configured for the running
    // consumer (tenant-enabled definitions), not a hard-coded list. Show the native (third-party) systems
    // in the marketing order design expects.
    const supportedAccountingSystems = React.useMemo(() => {
        return activeAccountingPackages
            .filter(pkg => pkg.packageType === 'thirdParty')
            .sort((p1, p2) => (p1.marketingSortOrder ?? 0) - (p2.marketingSortOrder ?? 0))
            .map(pkg => ({ name: pkg.friendlyName || '', isBeta: !!pkg.isBeta }))
            .filter(sys => !!sys.name);
    }, [activeAccountingPackages]);

    // The file-upload card's title comes from the friendlyName on the accounting package definition that
    // backs this card (nameId 'universalcollector', or 'fileupload' for the excel variant), which the
    // controller already returns in activeAccountingPackages. Fall back to the portal-text option label.
    const fileUploadTitle = React.useMemo(() => {
        const fileNameId = fileConnectorType === ConnectorType.universalConnector
            ? UniversalCollectorAccountingPackageNameId
            : ExcelUploadAccountingPackageNameId;
        const filePackage = activeAccountingPackages.find(pkg => (pkg.nameId || '').toLowerCase() === fileNameId.toLowerCase());
        return filePackage?.friendlyName || importModalsText?.fileUploadOption || '';
    }, [activeAccountingPackages, fileConnectorType, importModalsText]);

    const accountingSelected = connectorType === ConnectorType.fullIntegration;
    const fileSelected = connectorType === fileConnectorType;

    return (
        <>
            <div className={'page-grid-2col'}>
                {/* Connect to accounting system */}
                <label className={'cr-selectable-card page-hero-card'}>
                    <div className={'page-hero-card__banner'}>
                        <span className={`page-hero-card__icon${accountingSelected ? ' page-hero-card__icon--active' : ''}`}>
                            <FontAwesomeIcon icon={faPlug} />
                        </span>
                    </div>
                    <div className={'page-hero-card__body'}>
                        <span className={'cr-selectable-card__control'}>
                            <input
                                type={'radio'}
                                className={'cr-radio'}
                                name={'connection-type'}
                                id={'connector-type-full-integration'}
                                checked={accountingSelected}
                                onChange={() => onChangeConnectorType(ConnectorType.fullIntegration)}
                            />
                        </span>
                        <span className={'cr-selectable-card__content page-hero-card__content'}>
                            <span className={'cr-selectable-card__header'}>
                                <span className={'cr-selectable-card__heading page-hero-card__heading'}>
                                    {importModalsText?.connectToAccountingSystemOption || ''}
                                </span>
                            </span>
                            <span className={'cr-selectable-card__body'}>
                                <span className={'cr-selectable-card__description'}>
                                    {importModalsText?.connectToAccountingSystemDescription || ''}
                                </span>
                                {supportedAccountingSystems.length > 0 && (
                                    <>
                                        <span className={'page-hero-card__subheading'}>
                                            {importModalsText?.supportedAccountingSystems || ''}
                                        </span>
                                        <ul className={'page-hero-card__list'}>
                                            {supportedAccountingSystems.map(sys => (
                                                <li key={sys.name}>
                                                    {sys.name}
                                                    {sys.isBeta && (
                                                        <Tag
                                                            type={'secondary'}
                                                            outlined
                                                            style={{ marginLeft: 6, verticalAlign: 'middle' }}
                                                        >
                                                            Beta
                                                        </Tag>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </>
                                )}
                            </span>
                        </span>
                    </div>
                </label>

                {/* File upload */}
                <label className={'cr-selectable-card page-hero-card'}>
                    <div className={'page-hero-card__banner'}>
                        <span className={`page-hero-card__icon${fileSelected ? ' page-hero-card__icon--active' : ''}`}>
                            <FontAwesomeIcon icon={faFileArrowUp} />
                        </span>
                    </div>
                    <div className={'page-hero-card__body'}>
                        <span className={'cr-selectable-card__control'}>
                            <input
                                type={'radio'}
                                className={'cr-radio'}
                                name={'connection-type'}
                                id={'connector-type-universal-connector'}
                                checked={fileSelected}
                                onChange={() => onChangeConnectorType(fileConnectorType)}
                            />
                        </span>
                        <span className={'cr-selectable-card__content page-hero-card__content'}>
                            <span className={'cr-selectable-card__header'}>
                                <span className={'cr-selectable-card__heading page-hero-card__heading'}>
                                    {fileUploadTitle}
                                </span>
                            </span>
                            <span className={'cr-selectable-card__body'}>
                                <span className={'cr-selectable-card__description'}>
                                    {importModalsText?.fileUploadDescription || ''}
                                </span>
                                <span className={'page-hero-card__subheading'}>
                                    {importModalsText?.supportedFileTypes || ''}
                                </span>
                                <ul className={'page-hero-card__list'}>
                                    {SUPPORTED_FILE_TYPES.map(ft => (
                                        <li key={ft}>{ft}</li>
                                    ))}
                                </ul>
                            </span>
                        </span>
                    </div>
                </label>
            </div>
        </>
    );
};
